/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/

// Lightweight language detection focused on majority Chinese vs English.
// Heuristic: count Han characters vs Latin letters, decide primary language
// with a threshold. Designed for front-end usage without extra deps.

export type PrimaryLanguage = 'ZH_CN' | 'EN_US' | undefined;

const HAN_REGEX = /[\u3400-\u4DBF\u4E00-\u9FFF\uF900-\uFAFF]/g; // CJK Unified + Extension A + Compatibility
const LATIN_REGEX = /[A-Za-z]/g; // Basic Latin letters

export function detectPrimaryLanguage(text: string): PrimaryLanguage {
  if (!text) return undefined;

  const hanMatches = text.match(HAN_REGEX);
  const latinMatches = text.match(LATIN_REGEX);
  const hanCount = hanMatches ? hanMatches.length : 0;
  const latinCount = latinMatches ? latinMatches.length : 0;
  const total = hanCount + latinCount;

  // If there are almost no signal letters, return undefined
  if (total === 0) return undefined;

  const hanRatio = hanCount / total;

  // If most are Han, classify as Chinese; otherwise English
  if (hanRatio >= 0.6) return 'ZH_CN';
  if (latinCount >= hanCount) return 'EN_US';

  // Fallback when Han slightly dominates but below threshold
  return 'ZH_CN';
}

export function getAutoDirectionLanguages(
  text: string,
): { source: 'ZH_CN' | 'EN_US'; target: 'ZH_CN' | 'EN_US' } | undefined {
  const primary = detectPrimaryLanguage(text);
  if (!primary) return undefined;
  if (primary === 'ZH_CN') return { source: 'ZH_CN', target: 'EN_US' };
  return { source: 'EN_US', target: 'ZH_CN' };
}